import { FunctionComponent, useEffect, useState } from 'react'
import styles from './requestTable.module.scss'
import { useTranslation } from 'react-i18next'
import { Table, TableContainer } from '@mui/material'
import RequestTableRow from './requestTableRow'
import { Daycare } from '@/types/Daycare'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { Check, Close } from '@mui/icons-material'
import ConfirmRequest from '../Dialog/confirmRequest'
import { OccasionalSlotsPlaces, RegularCancellationRequest, RequestSlot } from '@/types/Slots'
import { ChildDaycareWithParents, ChildParent, ChildWithParents } from '@/types/Child'

interface RequestTableProps {
    requestList: (RegularCancellationRequest | RequestSlot)[]
    currentDaycare: Daycare
    refetchData: () => Promise<void>
    regularCancellationRequest: RegularCancellationRequest[]
    openFamily: boolean
    childs: ChildWithParents[]
    places: OccasionalSlotsPlaces[]
    canWriteContracts: boolean
    canWriteOccasionalSlot: boolean
}

const RequestTable: FunctionComponent<RequestTableProps> = ({
    requestList,
    currentDaycare,
    refetchData,
    regularCancellationRequest,
    openFamily,
    childs,
    places,
    canWriteContracts,
    canWriteOccasionalSlot
}: RequestTableProps) => {
    const { t } = useTranslation()

    const [parentsWithRequests, setParentsWithRequests] = useState<ChildParent[]>([])

    const [selectedRequests, setSelectedRequests] = useState<(RegularCancellationRequest | RequestSlot)[]>([])

    function hasRequest(parentId: number | undefined): boolean {
        return requestList.some(
            (request: RegularCancellationRequest | RequestSlot): boolean => request.parentId === parentId
        )
    }

    useEffect(() => {
        const parents: ChildParent[] = []

        childs.map((child: ChildWithParents): void => {
            child.daycares
                .find((daycare: ChildDaycareWithParents): boolean => daycare.daycareId === currentDaycare.id)
                ?.parents.map((parent: ChildParent): void => {
                    if (!parents.find((p: ChildParent): boolean => p.parentId === parent.parentId)) {
                        parents.push(parent)
                    }
                })
        })

        setParentsWithRequests(parents.filter((parent: ChildParent) => hasRequest(parent.parentId)))
    }, [childs, requestList])

    const [isShowRequest, setIsShowRequest] = useState<boolean>(false)
    const [isAcceptRequest, setIsAcceptRequest] = useState<boolean>(false)

    function handleShowRequestSlot(accept: boolean): void {
        setIsAcceptRequest(accept)
        setIsShowRequest(true)
    }

    async function handleRefetchAndResetSelected(): Promise<void> {
        setSelectedRequests([])
        await refetchData()
    }

    useEffect(() => {
        setSelectedRequests([])
    }, [currentDaycare.id])

    return (
        <>
            <div className={styles.RequestsPosition}>
                <div className={styles.RequestsContainer}>
                    {requestList.length > 0 ? (
                        <TableContainer className={styles.TablePlacement}>
                            <Table className={styles.Table}>
                                {parentsWithRequests.map((parent: ChildParent, index: number) => {
                                    return (
                                        <RequestTableRow
                                            key={index}
                                            parent={parent}
                                            requestList={requestList}
                                            selectedRequests={selectedRequests}
                                            setSelectedRequests={setSelectedRequests}
                                            currentDaycare={currentDaycare}
                                            refetchData={handleRefetchAndResetSelected}
                                            regularCancellationRequest={regularCancellationRequest}
                                            childs={childs}
                                            openFamily={openFamily}
                                            places={places}
                                            canWriteContracts={canWriteContracts}
                                            canWriteOccasionalSlot={canWriteOccasionalSlot}
                                        />
                                    )
                                })}
                            </Table>
                        </TableContainer>
                    ) : (
                        <p className={styles.NoRequests}>{t('noWaitingRequests')}</p>
                    )}
                </div>
                {selectedRequests.length > 0 && (canWriteContracts || canWriteOccasionalSlot) && (
                    <div className={styles.SelectedRequests}>
                        <ButtonTextIcon
                            type={'custom'}
                            onClick={() => handleShowRequestSlot(true)}
                            customIcon={<Check />}
                            customText={t('accept')}
                            className={styles.Button}
                        />
                        <ButtonTextIcon
                            type={'custom'}
                            onClick={() => handleShowRequestSlot(false)}
                            customIcon={<Close />}
                            customText={t('refuse')}
                            className={styles.Button}
                        />
                    </div>
                )}
            </div>
            {isShowRequest && (
                <ConfirmRequest
                    open={isShowRequest}
                    handleClose={() => setIsShowRequest(false)}
                    acceptRequest={isAcceptRequest}
                    selectedRequests={selectedRequests}
                    currentDaycare={currentDaycare}
                    childs={childs}
                    places={places}
                    refetchData={handleRefetchAndResetSelected}
                />
            )}
        </>
    )
}

export default RequestTable
